<template>
  <v-container fluid class="pa-4">
    <!-- Header -->
    <v-row class="mb-2 align-center justify-space-between">
      <v-col cols="12" md="6" class="d-flex align-center">
        <v-icon color="indigo darken-2" large class="mr-3">mdi-rocket-launch</v-icon>
        <div>
          <h2 class="headline font-weight-bold indigo--text text--darken-3">Backlog de Ejecución y Seguimiento TI</h2>
          <div class="caption text-uppercase grey--text text--darken-1">Sprints de 15 días con asignación de Historias de Usuario</div>
        </div>
      </v-col>
      <v-col cols="12" md="6" class="text-right d-flex align-center justify-end">
        <v-btn color="indigo" dark class="elevation-2 mr-2" @click="loadSprints">
          <v-icon left>mdi-refresh</v-icon> Actualizar
        </v-btn>
        <v-btn color="teal darken-2" dark class="elevation-2" @click="openSprintDialog()">
          <v-icon left>mdi-plus</v-icon> Nuevo Sprint
        </v-btn>
      </v-col>
    </v-row>

    <!-- Sprints -->
    <v-card outlined class="pa-3 mb-4 rounded-lg grey lighten-5 elevation-1">
      <div class="d-flex align-center mb-2">
        <v-icon color="indigo darken-2" class="mr-2">mdi-flag-checkered</v-icon>
        <span class="subtitle-2 font-weight-bold indigo--text text--darken-3">SPRINTS DEL EQUIPO (cada sprint dura 15 días)</span>
      </div>
      <v-data-table
        :headers="sprintHeaders"
        :items="sprints"
        dense
        hide-default-footer
        class="elevation-0 transparent"
        no-data-text="Aún no hay sprints creados"
      >
        <template v-slot:item.TspStartDate="{ item }">
          <span>{{ formatDate(item.TspStartDate) }}</span>
        </template>
        <template v-slot:item.TspEndDate="{ item }">
          <span>{{ formatDate(item.TspEndDate) }}</span>
        </template>
        <template v-slot:item.TspStatus="{ item }">
          <v-chip x-small :color="getSprintStatusColor(item.TspStatus)" dark class="font-weight-bold">
            {{ getSprintStatusName(item.TspStatus) }}
          </v-chip>
        </template>
        <template v-slot:item.TspStoryPoints="{ item }">
          <v-chip x-small color="deep-purple lighten-4" class="deep-purple--text font-weight-bold">
            {{ item.TspStoryPoints || 0 }} pts
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <v-btn x-small text color="indigo" @click="selectSprint(item)">
              <v-icon x-small left>mdi-format-list-bulleted</v-icon> Historias
            </v-btn>
            <v-btn icon x-small color="teal" @click="openSprintDialog(item)">
              <v-icon x-small>mdi-pencil</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Historias del sprint seleccionado + backlog sin sprint -->
    <v-row v-if="selectedSprint || unassignedStories.length > 0">
      <!-- Columna: historias del sprint -->
      <v-col cols="12" md="8">
        <v-card outlined class="rounded-lg elevation-1 fill-height">
          <div class="pa-3 border-bottom d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="deep-purple darken-2" class="mr-2">mdi-book-open-variant</v-icon>
              <span class="subtitle-2 font-weight-bold">
                Historias del Sprint: {{ selectedSprint ? selectedSprint.TspName : '—' }}
              </span>
            </div>
            <v-chip x-small color="deep-purple lighten-4" class="deep-purple--text font-weight-bold">
              {{ sprintStories.length }} historia(s) · {{ sprintTotalPoints }} pts
            </v-chip>
          </div>
          <v-data-table
            :headers="storyHeaders"
            :items="sprintStories"
            dense
            hide-default-footer
            class="elevation-0 transparent"
            no-data-text="Este sprint aún no tiene historias asignadas. Asigne desde el panel 'Backlog sin Sprint'."
          >
            <template v-slot:item.UsoCommitmentDate="{ item }">
              <span>{{ formatDate(item.UsoCommitmentDate) }}</span>
            </template>
            <template v-slot:item.DeviationDays="{ item }">
              <v-chip small :color="getDeviationColor(item.DeviationDays)" dark class="font-weight-bold">
                {{ getComplianceLabel(item.DeviationDays) }}
              </v-chip>
            </template>
            <template v-slot:item.UsoStatusName="{ item }">
              <v-chip small :color="statusColor(item.UsoStatus)" dark class="font-weight-bold">
                {{ item.UsoStatusName || 'En Desarrollo' }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="d-flex justify-end">
                <v-btn icon x-small color="indigo" @click="openStoryDetail(item)" title="Detalle de ejecución">
                  <v-icon x-small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon x-small color="orange darken-2" @click="unassignStory(item)" title="Quitar del sprint">
                  <v-icon x-small>mdi-arrow-top-right-thick</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Columna: backlog sin sprint -->
      <v-col cols="12" md="4">
        <v-card outlined class="rounded-lg elevation-1 fill-height">
          <div class="pa-3 border-bottom d-flex align-center">
            <v-icon color="teal darken-2" class="mr-2">mdi-tray-arrow-down</v-icon>
            <span class="subtitle-2 font-weight-bold">Backlog sin Sprint</span>
          </div>
          <div class="pa-2" style="max-height: 420px; overflow-y: auto">
            <v-card v-for="story in unassignedStories" :key="story.UsoID" outlined class="pa-2 mb-2 rounded-lg">
              <div class="d-flex justify-space-between align-start">
                <div>
                  <span class="caption font-weight-bold primary--text">{{ story.UsoCode }}</span>
                  <span v-if="story.UsoStoryPoints" class="caption deep-purple--text font-weight-bold ml-1">{{ story.UsoStoryPoints }} pts</span>
                  <div class="body-2 font-weight-bold text--primary line-clamp-2">{{ story.UsoTitle }}</div>
                  <div class="caption grey--text">{{ story.ReqCode }} - {{ story.ReqTitle }}</div>
                </div>
              </div>
              <div class="d-flex justify-end mt-1">
                <v-btn
                  x-small
                  color="teal darken-2"
                  dark
                  :disabled="!selectedSprint"
                  @click="assignStory(story)"
                >
                  <v-icon x-small left>mdi-plus</v-icon> Asignar al Sprint
                </v-btn>
              </div>
            </v-card>
            <div v-if="unassignedStories.length === 0" class="text-center pa-4">
              <span class="caption grey--text">No hay historias pendientes de sprint</span>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog: Nuevo / Editar Sprint -->
    <v-dialog v-model="sprintDialog" max-width="550px" persistent>
      <v-card v-if="sprintItem" class="rounded-lg">
        <v-card-title class="headline indigo lighten-5 indigo--text text--darken-3 d-flex align-center">
          <v-icon color="indigo" class="mr-2">mdi-flag-checkered</v-icon>
          {{ sprintItem && sprintItem.TspID ? 'Editar Sprint' : 'Nuevo Sprint (15 días)' }}
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row dense>
            <v-col cols="12">
              <s-text label="Nombre del Sprint *" v-model="sprintItem.TspName" placeholder="Ej: Sprint 2026-09"></s-text>
            </v-col>
            <v-col cols="12" sm="6">
              <s-date label="Fecha Inicio *" v-model="sprintItem.TspStartDate" @change="recalculateEndDate"></s-date>
            </v-col>
            <v-col cols="12" sm="6">
              <s-date label="Fecha Fin *" v-model="sprintItem.TspEndDate"></s-date>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="sprintItem.TspStatus"
                :items="sprintStatusOptions"
                label="Estado del Sprint"
                dense
                outlined
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" class="d-flex align-center">
              <v-chip x-small color="deep-purple lighten-4" class="deep-purple--text font-weight-bold">
                Puntos del sprint: {{ sprintItem.TspStoryPoints || 0 }} (calculados de sus historias)
              </v-chip>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-3 grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="sprintDialog = false">Cancelar</v-btn>
          <v-btn color="indigo" dark :loading="savingSprint" @click="saveSprint">
            <v-icon left>mdi-content-save</v-icon> Guardar Sprint
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Detalle de Ejecución de la Historia -->
    <v-dialog v-model="storyDetailDialog" max-width="900px" persistent>
      <v-card v-if="storyDetail" class="rounded-lg">
        <v-card-title class="headline indigo lighten-5 indigo--text text--darken-3">
          <v-icon color="indigo" class="mr-2">mdi-book-open-variant</v-icon>
          {{ storyDetail.UsoCode }} - {{ storyDetail.UsoTitle }}
        </v-card-title>

        <v-card-text class="pa-4">
          <v-card outlined class="pa-3 mb-3 grey lighten-5 rounded-lg">
            <div class="d-flex align-center flex-wrap">
              <span class="caption font-weight-bold primary--text mr-3">{{ storyDetail.ReqCode }}</span>
              <span class="caption grey--text text--darken-1 mr-3">{{ storyDetail.ReqTitle }}</span>
              <v-chip v-if="storyDetail.UsoFinalSizeName" x-small color="deep-purple lighten-4" class="deep-purple--text font-weight-bold mr-2">
                {{ storyDetail.UsoFinalSizeName }} · {{ storyDetail.UsoStoryPoints }} pts
              </v-chip>
              <v-chip x-small :color="statusColor(storyDetail.UsoStatus)" dark class="font-weight-bold">
                {{ storyDetail.UsoStatusName || 'En Desarrollo' }}
              </v-chip>
            </div>
          </v-card>

          <v-tabs v-model="activeTab" background-color="transparent" color="indigo darken-3">
            <v-tab><v-icon left small>mdi-code-tags</v-icon>Desarrollo</v-tab>
            <v-tab><v-icon left small>mdi-bug-check</v-icon>QA & UAT</v-tab>
            <v-tab><v-icon left small>mdi-server-network</v-icon>Producción</v-tab>
            <v-tab @click="loadStoryHistory"><v-icon left small>mdi-history</v-icon>Historial Auditoría</v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab" class="pt-4">
            <!-- Tab 1: Desarrollo -->
            <v-tab-item>
              <v-row>
                <v-col cols="12" sm="6">
                  <s-date label="Fecha Compromiso" v-model="storyDetail.UsoCommitmentDate"></s-date>
                </v-col>
                <v-col cols="12" sm="6">
                  <s-date label="Fecha Inicio Desarrollo" v-model="storyDetail.UsoDevStartDate"></s-date>
                </v-col>
                <v-col cols="12" sm="6">
                  <s-select-user label="Desarrollador Responsable TI" v-model="storyDetail.UsoAssignedIT"></s-select-user>
                </v-col>
                <v-col cols="12" sm="6">
                  <s-select-definition
                    label="Estado (se cambia en el Tablero Kanban)"
                    :def="1721"
                    v-model="storyDetail.UsoStatus"
                    disabled
                  ></s-select-definition>
                </v-col>
                <v-col cols="12">
                  <s-textarea label="Observaciones de Ejecución" v-model="storyDetail.UsoObservations"></s-textarea>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- Tab 2: QA & UAT -->
            <v-tab-item>
              <v-card outlined class="pa-3 mb-3 grey lighten-5 rounded-lg">
                <div class="subtitle-2 font-weight-bold mb-2 primary--text">Control de Calidad (QA)</div>
                <v-row>
                  <v-col cols="12" sm="4">
                    <s-date label="Fecha Inicio QA" v-model="storyDetail.UsoQAStartDate"></s-date>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <s-select-user label="Responsable QA" v-model="storyDetail.UsoQAResponsible"></s-select-user>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <s-select-definition label="Resultado QA" :def="1727" v-model="storyDetail.UsoQAResult"></s-select-definition>
                  </v-col>
                  <v-col cols="12">
                    <s-text label="Enlace / Ruta de Evidencia QA" v-model="storyDetail.UsoQAEvidence"></s-text>
                  </v-col>
                </v-row>
              </v-card>

              <v-card outlined class="pa-3 grey lighten-5 rounded-lg">
                <div class="subtitle-2 font-weight-bold mb-2 teal--text text--darken-2">Pruebas de Aceptación de Usuario (UAT)</div>
                <v-row>
                  <v-col cols="12" sm="4">
                    <s-date label="Fecha Inicio UAT" v-model="storyDetail.UsoUATStartDate"></s-date>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <s-select-user label="Responsable UAT (Usuario / PO)" v-model="storyDetail.UsoUATResponsible"></s-select-user>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <s-date label="Fecha Aprobación UAT" v-model="storyDetail.UsoUATApprovalDate"></s-date>
                  </v-col>
                  <v-col cols="12">
                    <s-text label="Evidencia / Conformidad UAT" v-model="storyDetail.UsoUATEvidence"></s-text>
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>

            <!-- Tab 3: Producción -->
            <v-tab-item>
              <v-row>
                <v-col cols="12" sm="4">
                  <s-date label="Fecha Listo para Producción" v-model="storyDetail.UsoReadyForProdDate"></s-date>
                </v-col>
                <v-col cols="12" sm="4">
                  <s-text label="Versión de Despliegue" v-model="storyDetail.UsoDeployVersion" placeholder="v1.2.0"></s-text>
                </v-col>
                <v-col cols="12" sm="4">
                  <s-date label="Fecha Despliegue en Producción" v-model="storyDetail.UsoProdDate"></s-date>
                </v-col>
                <v-col cols="12" sm="6">
                  <s-select-user label="Desplegado Por" v-model="storyDetail.UsoDeployedBy"></s-select-user>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- Tab 4: Historial de Auditoría -->
            <v-tab-item>
              <v-data-table
                :headers="historyHeaders"
                :items="historyItems"
                :loading="loadingHistory"
                dense
                class="elevation-1 rounded-lg"
                no-data-text="Sin historial registrado para esta historia"
              >
                <template v-slot:item.SecCreate="{ item }">
                  <span>{{ formatDate(item.SecCreate) }}</span>
                </template>
              </v-data-table>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>

        <v-card-actions class="pa-3 grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="storyDetailDialog = false">Cancelar</v-btn>
          <v-btn color="indigo" dark :loading="savingStory" @click="saveStoryDetail">
            <v-icon left>mdi-content-save</v-icon> Guardar Ejecución
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import _sSprintService from "@/services/InformationTechnology/TicSprintService.js";
import _sUserStoryService from "@/services/InformationTechnology/TicUserStoryService.js";
import sTextarea from '../../../components/Utils/sTextarea.vue';
import SSelectUser from "@/components/Utils/SSelectUser.vue";
import { statusColor } from './statusColors.js';

export default {
  components: { sTextarea, SSelectUser },
  data() {
    return {
      sprints: [],
      selectedSprint: null,
      sprintStories: [],
      unassignedStories: [],
      sprintDialog: false,
      sprintItem: null,
      savingSprint: false,
      storyDetailDialog: false,
      storyDetail: null,
      savingStory: false,
      activeTab: 0,
      historyItems: [],
      loadingHistory: false,
      sprintStatusOptions: [
        { text: "Creado", value: 1 },
        { text: "Activo", value: 2 },
        { text: "Completado", value: 3 }
      ],
      sprintHeaders: [
        { text: "Sprint", value: "TspName", width: "220px" },
        { text: "Inicio", value: "TspStartDate", width: "120px" },
        { text: "Fin", value: "TspEndDate", width: "120px" },
        { text: "Estado", value: "TspStatus", align: "center", width: "120px" },
        { text: "Puntos", value: "TspStoryPoints", align: "center", width: "110px" },
        { text: "Acciones", value: "actions", align: "center", width: "180px", sortable: false }
      ],
      storyHeaders: [
        { text: "Código", value: "UsoCode", width: "120px" },
        { text: "Historia de Usuario", value: "UsoTitle", width: "240px" },
        { text: "Requerimiento", value: "ReqCode", width: "130px" },
        { text: "Puntos", value: "UsoStoryPoints", align: "center", width: "80px" },
        { text: "Asignado TI", value: "AssignedITName", width: "150px" },
        { text: "Compromiso", value: "UsoCommitmentDate", width: "120px" },
        { text: "Cumplimiento", value: "DeviationDays", align: "center", width: "150px" },
        { text: "Estado", value: "UsoStatusName", align: "center", width: "150px" },
        { text: "Acciones", value: "actions", align: "center", width: "110px", sortable: false }
      ],
      historyHeaders: [
        { text: "Fecha Registro", value: "SecCreate", width: "160px" },
        { text: "Usuario", value: "CreatorName", width: "180px" },
        { text: "Estado Anterior", value: "OldStatusName", width: "150px" },
        { text: "Nuevo Estado", value: "NewStatusName", width: "150px" },
        { text: "Comentario Auditoría", value: "UshComment" }
      ]
    };
  },
  created() {
    this.loadSprints();
    this.loadUnassignedStories();
  },
  computed: {
    sprintTotalPoints() {
      return this.sprintStories.reduce((sum, s) => sum + (Number(s.UsoStoryPoints) || 0), 0);
    }
  },
  methods: {
    statusColor(status) {
      return statusColor(status);
    },
    formatDate(date) {
      if (!date) return '-';
      return this.$moment(date).format("DD/MM/YYYY");
    },
    getSprintStatusName(status) {
      const option = this.sprintStatusOptions.find(o => o.value === status);
      return option ? option.text : 'Creado';
    },
    getSprintStatusColor(status) {
      switch (status) {
        case 2: return "green darken-1";
        case 3: return "blue-grey darken-1";
        default: return "indigo";
      }
    },
    getDeviationColor(days) {
      if (days == null) return "grey";
      if (days >= 0) return "green darken-1";
      if (days >= -3) return "orange darken-2";
      return "red darken-1";
    },
    getComplianceLabel(days) {
      if (days == null) return '-';
      if (days < 0) return `Vencido (${days} d)`;
      if (days === 0) return 'Vence Hoy (0 d)';
      return `En Plazo (+${days} d)`;
    },

    loadSprints() {
      _sSprintService.list({}, Number(this.$fun.getUserID()))
        .then(response => {
          this.sprints = response.data || [];
          console.log("SprintBacklog: sprints recibidos:", this.sprints.length, this.sprints);
          if (!this.selectedSprint && this.sprints.length > 0) {
            // Preseleccionar el sprint activo o el más reciente
            const active = this.sprints.find(s => s.TspStatus === 2);
            this.selectSprint(active || this.sprints[0]);
          }
        })
        .catch(() => {
          this.sprints = [];
        });
    },

    selectSprint(sprint) {
      this.selectedSprint = sprint;
      this.loadSprintStories(sprint);
    },

    loadSprintStories(sprint) {
      if (!sprint || !sprint.TspID) {
        this.sprintStories = [];
        return;
      }
      const payload = {
        start: 0,
        length: 300,
        search: { value: "" },
        filter: {
          UsoSprintID: sprint.TspID,
          ExcludeTerminalStates: false
        }
      };
      _sUserStoryService.pagination(payload, Number(this.$fun.getUserID()))
        .then(response => {
          this.sprintStories = (response.data && response.data.data) || [];
          console.log("SprintBacklog: historias del sprint recibidas:", this.sprintStories.length, this.sprintStories);
          if (this.unassignedStories && this.unassignedStories.length > 0) {
            this.unassignedStories = this.unassignedStories.filter(u => !this.sprintStories.some(s => s.UsoID === u.UsoID));
          }
        })
        .catch(() => {
          this.sprintStories = [];
        });
    },

    loadUnassignedStories() {
      const payload = {
        start: 0,
        length: 300,
        search: { value: "" },
        filter: {
          UsoSprintID: -1, // En TicUserStory_P: -1 filtra estrictamente (S.UsoSprintID IS NULL OR S.UsoSprintID = 0)
          ExcludeTerminalStates: true
        }
      };
      _sUserStoryService.pagination(payload, Number(this.$fun.getUserID()))
        .then(response => {
          const stories = (response.data && response.data.data) || [];
          console.log("SprintBacklog: historias sin sprint recibidas:", stories.length, stories);
          // Solo historias listas para ejecución (estado >= 3) que NO tengan sprint asignado
          this.unassignedStories = stories.filter(s =>
            s.UsoStatus >= 3 &&
            (!s.UsoSprintID || s.UsoSprintID === 0 || s.UsoSprintID === null) &&
            (!this.sprintStories || !this.sprintStories.some(ss => ss.UsoID === s.UsoID))
          );
        })
        .catch(() => {
          this.unassignedStories = [];
        });
    },

    assignStory(story) {
      if (!this.selectedSprint) {
        this.$fun.alert("Seleccione un sprint primero", "warning");
        return;
      }
      const currentUserID = Number(this.$fun.getUserID());
      // Quitar de inmediato de la lista sin sprint (actualización optimista)
      this.unassignedStories = this.unassignedStories.filter(s => s.UsoID !== story.UsoID);

      _sUserStoryService.assignSprint(
        { UsoID: story.UsoID, TspID: this.selectedSprint.TspID, UsrUpdateID: currentUserID },
        currentUserID
      ).then(response => {
        if (response.status === 200) {
          this.$fun.alert("Historia asignada al sprint correctamente", "success");
          this.loadSprintStories(this.selectedSprint);
          this.loadUnassignedStories();
          this.loadSprints();
        }
      }).catch(error => {
        this.$fun.alert(error?.response?.data?.Message || "Error al asignar la historia al sprint", "error");
        this.loadUnassignedStories();
      });
    },

    unassignStory(story) {
      const currentUserID = Number(this.$fun.getUserID());
      _sUserStoryService.unassignSprint(
        { UsoID: story.UsoID, UsrUpdateID: currentUserID },
        currentUserID
      ).then(response => {
        if (response.status === 200) {
          this.$fun.alert("Historia removida del sprint", "success");
          this.loadSprintStories(this.selectedSprint);
          this.loadUnassignedStories();
          this.loadSprints();
        }
      }).catch(error => {
        this.$fun.alert(error?.response?.data?.Message || "Error al remover la historia del sprint", "error");
      });
    },

    nextMondayOrDefault(date) {
      const d = this.$moment(date || new Date());
      const day = d.day(); // 0 domingo .. 6 sábado
      if (day === 1) return d;
      if (day === 0) return d.add(1, 'days');
      return d.add(8 - day, 'days');
    },

    openSprintDialog(sprint) {
      if (sprint && sprint.TspID) {
        this.sprintItem = Object.assign({}, sprint);
      } else {
        const start = this.nextMondayOrDefault();
        this.sprintItem = {
          TspID: null,
          TspName: "",
          TspStartDate: start.format("YYYY-MM-DD"),
          TspEndDate: start.clone().add(14, 'days').format("YYYY-MM-DD"),
          TspStatus: 1,
          TspStoryPoints: 0,
          SecStatus: 1
        };
      }
      this.sprintDialog = true;
    },

    recalculateEndDate() {
      if (this.sprintItem && this.sprintItem.TspStartDate) {
        this.sprintItem.TspEndDate = this.$moment(this.sprintItem.TspStartDate).add(14, 'days').format("YYYY-MM-DD");
      }
    },

    saveSprint() {
      if (!this.sprintItem) return;
      if (!this.sprintItem.TspName || !this.sprintItem.TspName.trim()) {
        this.$fun.alert("El nombre del sprint es obligatorio", "warning");
        return;
      }
      if (!this.sprintItem.TspStartDate || !this.sprintItem.TspEndDate) {
        this.$fun.alert("Las fechas de inicio y fin son obligatorias", "warning");
        return;
      }

      const currentUserID = Number(this.$fun.getUserID());
      this.sprintItem.UsrCreateID = this.sprintItem.TspID ? this.sprintItem.UsrCreateID : currentUserID;

      this.savingSprint = true;
      _sSprintService.save(this.sprintItem, currentUserID)
        .then(response => {
          if (response.status === 200) {
            this.$fun.alert("Sprint guardado correctamente", "success");
            this.sprintDialog = false;
            this.loadSprints();
          }
        })
        .catch(error => {
          this.$fun.alert(error?.response?.data?.Message || "Error al guardar el sprint", "error");
        })
        .finally(() => {
          this.savingSprint = false;
        });
    },

    openStoryDetail(story) {
      this.storyDetail = Object.assign({}, story);
      this.activeTab = 0;
      this.historyItems = [];
      this.storyDetailDialog = true;
    },

    loadStoryHistory() {
      if (!this.storyDetail || !this.storyDetail.UsoID) return;
      this.loadingHistory = true;
      _sUserStoryService.getHistory(this.storyDetail.UsoID, Number(this.$fun.getUserID()))
        .then(response => {
          if (response.status === 200) {
            this.historyItems = response.data || [];
          }
        })
        .catch(() => {
          this.historyItems = [];
        })
        .finally(() => {
          this.loadingHistory = false;
        });
    },

    saveStoryDetail() {
      if (!this.storyDetail || !this.storyDetail.UsoID) return;
      const currentUserID = Number(this.$fun.getUserID());
      this.storyDetail.UsrUpdateID = currentUserID;

      this.savingStory = true;
      _sUserStoryService.save(this.storyDetail, currentUserID)
        .then(response => {
          if (response.status === 200) {
            this.$fun.alert("Datos de ejecución guardados correctamente", "success");
            this.storyDetailDialog = false;
            this.loadSprintStories(this.selectedSprint);
          }
        })
        .catch(error => {
          this.$fun.alert(error?.response?.data?.Message || "Error al guardar la historia", "error");
        })
        .finally(() => {
          this.savingStory = false;
        });
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}
</style>
